<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { useTutor } from '../composables/useTutor';
import { trpc } from '../trpc';
import AppLayout from '@/components/layout/AppLayout.vue';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-vue-next';
import { marked } from 'marked';

marked.setOptions({ async: false });

function normalizeMarkdown(text: string): string {
  return text
    .replace(/(?<!\n)\n(#{1,6} )/g, '\n\n$1')
    .replace(/(?<!\n)\n(---+|\*\*\*+|___+)\s*$/gm, '\n\n$1')
    .replace(/([^\n])\n([-*] |\d+\. )/g, '$1\n\n$2');
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const {
  messages,
  isStreaming,
  isLoadingSession,
  error,
  lessonCompleted,
  lessonAdvanced,
  lessonSkipChange,
  curriculumCompleted,
  sendMessage,
  initCurriculumSession,
} = useTutor();

const inputText = ref('');
const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

interface Lesson {
  id: string;
  title: string;
  content: string | null;
  order: number;
  state: string;
  skipped: boolean;
  lessonGoals: string[];
}

const curriculumId = computed(() => route.params.id as string);
const curriculumTitle = ref('');
const lessonList = ref<Lesson[]>([]);
const activeLessonId = ref('');
const dataLoading = ref(true);

const activeLesson = computed(() =>
  lessonList.value.find((l) => l.id === activeLessonId.value)
);

const lessonMessages = computed(() =>
  messages.value.filter((m) => m.lessonId === activeLessonId.value)
);

const nextLesson = computed(() => {
  if (!activeLesson.value) return null;
  const currentOrder = activeLesson.value.order;
  return (
    lessonList.value.find(
      (l) => l.order > currentOrder && l.state !== 'passed' && !l.skipped
    ) ?? null
  );
});

const showContinueButton = computed(
  () =>
    activeLesson.value?.state === 'passed' &&
    !!nextLesson.value &&
    !isStreaming.value
);

// Once a lesson is passed, choosing what to retain happens in a dedicated retention
// chat, not in the learning transcript.
const showRetentionHandoff = computed(
  () => activeLesson.value?.state === 'passed' && !isStreaming.value
);

function goToRetention() {
  if (!activeLessonId.value) return;
  router.push({
    path: `/learn/${curriculumId.value}/retention`,
    query: { lessonId: activeLessonId.value },
  });
}

function hasMessagesForLesson(lessonId: string): boolean {
  return messages.value.some((m) => m.lessonId === lessonId);
}

function isLessonUnlocked(lesson: Lesson): boolean {
  return lessonList.value
    .filter((l) => l.order < lesson.order)
    .every((l) => l.state === 'passed' || l.skipped);
}

function pickDefaultLesson(): string | null {
  const lesson =
    lessonList.value.find((l) => l.state === 'teaching' && !l.skipped) ??
    lessonList.value.find((l) => l.state !== 'passed' && !l.skipped) ??
    lessonList.value[0];
  return lesson?.id ?? null;
}

function syncLessonUrl(lessonId: string) {
  if (route.query.lessonId !== lessonId) {
    router.replace({ query: { ...route.query, lessonId } });
  }
}

async function selectLesson(lessonId: string) {
  if (lessonId === activeLessonId.value || isStreaming.value) return;
  const lesson = lessonList.value.find((l) => l.id === lessonId);
  if (!lesson || !isLessonUnlocked(lesson)) return;
  activeLessonId.value = lessonId;
  syncLessonUrl(lessonId);
  scrollToBottom();
  if (!hasMessagesForLesson(lessonId)) {
    await kickOffLesson(lessonId);
  }
}

async function kickOffLesson(lessonId: string) {
  await sendMessage(
    "I'm ready to start learning. Please begin the lesson.",
    curriculumId.value,
    lessonId
  );
}

async function continueToNextLesson() {
  const next = nextLesson.value;
  if (!next || isStreaming.value) return;
  try {
    await trpc.updateLesson.mutate({ id: next.id, state: 'teaching' });
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to advance lesson';
    return;
  }
  await fetchLessons();
  await selectLesson(next.id);
}

const expandedFlashcards = reactive(new Set<string>());

function toggleFlashcard(id: string) {
  if (expandedFlashcards.has(id)) {
    expandedFlashcards.delete(id);
  } else {
    expandedFlashcards.add(id);
  }
}

function toolLabel(name: string, status: string): string {
  const running = status === 'running';
  switch (name) {
    case 'create_flashcard':
      return running ? 'Creating flashcard…' : 'Flashcard created';
    case 'update_retention_intent':
      return running ? 'Updating retention intent…' : 'Retention intent updated';
    case 'mark_lesson_complete':
      return running ? 'Marking lesson complete…' : 'Lesson marked complete';
    case 'advance_to_next_lesson':
      return running ? 'Advancing to next lesson…' : 'Advanced to next lesson';
    default:
      return running ? 'Working…' : 'Done';
  }
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

function autoResize(event: Event) {
  const el = event.target as HTMLTextAreaElement;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
  scrollToBottom();
}

async function handleSubmit() {
  if (!inputText.value.trim() || isStreaming.value) return;
  const text = inputText.value;
  inputText.value = '';
  if (textareaRef.value) textareaRef.value.style.height = 'auto';
  scrollToBottom();
  await sendMessage(text, curriculumId.value, activeLessonId.value);
  scrollToBottom();
}

watch(
  () => messages.value.map((m) => m.content).join(''),
  () => scrollToBottom()
);

watch(lessonAdvanced, (advanced) => {
  if (advanced) {
    activeLessonId.value = advanced.lessonId;
    syncLessonUrl(advanced.lessonId);
    fetchLessons();
  }
});

watch(lessonCompleted, async (done) => {
  if (!done) return;
  await fetchLessons();
  lessonCompleted.value = false;
});

watch(lessonSkipChange, (change) => {
  if (change) fetchLessons();
});

watch(curriculumCompleted, (done) => {
  if (done) fetchLessons();
});

async function fetchLessons() {
  try {
    const curriculum = await trpc.getCurriculum.query({ id: curriculumId.value });
    if (!curriculum) throw new Error('Curriculum not found');
    curriculumTitle.value = curriculum.title;

    const lessonsList = await trpc.listLessons.query({ curriculumId: curriculumId.value });
    lessonList.value = lessonsList.sort(
      (a: { order: number }, b: { order: number }) => a.order - b.order
    );
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load curriculum';
  }
}

onMounted(async () => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
    return;
  }

  dataLoading.value = true;
  await fetchLessons();
  await initCurriculumSession(curriculumId.value);

  const lessonIdParam = route.query.lessonId as string | undefined;
  const requestedLesson = lessonIdParam
    ? lessonList.value.find((l) => l.id === lessonIdParam)
    : undefined;
  const requested = requestedLesson && isLessonUnlocked(requestedLesson)
    ? requestedLesson.id
    : null;
  const targetLessonId = requested ?? pickDefaultLesson();

  if (!targetLessonId) {
    error.value = 'No lessons available';
    dataLoading.value = false;
    return;
  }

  activeLessonId.value = targetLessonId;
  syncLessonUrl(targetLessonId);
  dataLoading.value = false;
  scrollToBottom();

  if (!hasMessagesForLesson(targetLessonId)) {
    await kickOffLesson(targetLessonId);
    scrollToBottom();
  }
});
</script>

<template>
  <AppLayout>
    <template #nav>
      <!-- Back link + curriculum title -->
      <div class="px-3 pt-2 pb-3">
        <button
          class="text-xs text-muted-foreground hover:text-foreground mb-3"
          @click="router.push(`/learn/${curriculumId}`)"
        >
          &larr; Back to Curriculum
        </button>
        <h2 class="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
          {{ curriculumTitle }}
        </h2>
      </div>

      <!-- Lesson list -->
      <div class="px-2 pb-2">
        <div class="space-y-0.5">
          <div
            v-for="lesson in lessonList"
            :key="lesson.id"
            class="text-sm"
          >
            <button
              type="button"
              class="flex items-center gap-2 w-full text-left rounded-md px-2 py-1.5 transition-colors disabled:cursor-not-allowed"
              :class="[
                lesson.id === activeLessonId
                  ? 'bg-accent text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                lesson.skipped ? 'opacity-60' : '',
                !isLessonUnlocked(lesson) ? 'opacity-50 hover:bg-transparent hover:text-muted-foreground' : '',
              ]"
              :disabled="(isStreaming && lesson.id !== activeLessonId) || !isLessonUnlocked(lesson)"
              :title="!isLessonUnlocked(lesson) ? 'Complete the previous lesson first' : undefined"
              @click="selectLesson(lesson.id)"
            >
              <span
                class="font-mono text-xs w-4 shrink-0"
                :class="lesson.skipped ? 'line-through' : ''"
              >{{ lesson.order + 1 }}</span>
              <span
                class="flex-1 truncate"
                :class="lesson.skipped ? 'line-through' : ''"
              >{{ lesson.title }}</span>
              <span
                v-if="lesson.skipped"
                class="text-xs italic"
              >skipped</span>
              <span
                v-else-if="lesson.state === 'passed'"
                class="text-green-600 dark:text-green-400 text-xs"
              >&#10003;</span>
              <Lock
                v-else-if="!isLessonUnlocked(lesson)"
                class="size-3 shrink-0"
              />
            </button>

            <!-- Show goals for current lesson -->
            <div
              v-if="lesson.id === activeLessonId && lesson.lessonGoals.length > 0"
              class="ml-8 space-y-1 mt-1 mb-2"
            >
              <div
                v-for="(goal, goalIdx) in lesson.lessonGoals"
                :key="goalIdx"
                class="flex items-start gap-1.5 text-xs text-muted-foreground"
              >
                <span class="mt-0.5 shrink-0">&#8226;</span>
                <span class="flex-1">{{ goal }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #mobile-top>
      <div class="md:hidden px-4 py-2 flex items-center justify-between shrink-0 border-b border-border">
        <button
          class="text-sm text-muted-foreground hover:text-foreground"
          @click="router.push(`/learn/${curriculumId}`)"
        >
          &larr; Back
        </button>
        <span
          v-if="activeLesson"
          class="text-sm font-medium truncate ml-3"
        >
          Lesson {{ activeLesson.order + 1 }}: {{ activeLesson.title }}
        </span>
      </div>
    </template>

    <!-- Loading state -->
    <div
      v-if="isLoadingSession || dataLoading"
      class="flex-1 flex items-center justify-center text-muted-foreground"
    >
      Loading...
    </div>

    <template v-else>
      <!-- Messages area -->
      <div
        ref="messagesContainer"
        class="flex-1 overflow-y-auto scrollbar-thin"
      >
        <div class="max-w-3xl mx-auto w-full px-6 py-6 space-y-6">
          <div
            v-for="(message, msgIdx) in lessonMessages"
            :key="msgIdx"
            class="flex"
            :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              :class="
                message.role === 'user'
                  ? 'max-w-[80%] rounded-2xl px-4 py-2 bg-primary text-primary-foreground'
                  : 'w-full text-foreground'
              "
            >
              <div
                v-if="message.role === 'assistant'"
                class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                v-html="marked.parse(normalizeMarkdown(message.content.trim()))"
              />
              <div
                v-else
                class="whitespace-pre-wrap"
              >
                {{ message.content.trim() }}
              </div>
              <span
                v-if="message.isStreaming && !message.content"
                class="inline-flex gap-1"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
              </span>
              <span
                v-else-if="message.isStreaming"
                class="inline-block w-0.5 h-4 bg-current animate-pulse ml-0.5 align-text-bottom"
              />
              <div
                v-if="message.role === 'assistant' && message.toolUses?.length"
                class="mt-2 flex flex-wrap gap-1.5"
              >
                <div
                  v-for="tu in message.toolUses"
                  :key="tu.id"
                  class="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-border bg-background/60"
                  :class="tu.status === 'error' ? 'text-destructive' : 'text-muted-foreground'"
                >
                  <span
                    v-if="tu.status === 'running'"
                    class="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
                  />
                  <span
                    v-else-if="tu.status === 'done'"
                    class="text-green-600 dark:text-green-400"
                  >&#10003;</span>
                  <span v-else>&#9888;</span>
                  {{ toolLabel(tu.name, tu.status) }}
                </div>
              </div>
              <div
                v-if="message.role === 'assistant' && message.flashcards?.length"
                class="mt-3 space-y-2"
              >
                <div
                  v-for="card in message.flashcards"
                  :key="card.id"
                  class="rounded-md border border-border bg-background/60 px-3 py-2 text-xs"
                >
                  <button
                    type="button"
                    class="w-full text-left flex items-start gap-2 hover:text-foreground"
                    @click="toggleFlashcard(card.id)"
                  >
                    <span class="mt-0.5 shrink-0">&#128221;</span>
                    <div class="flex-1 min-w-0">
                      <div class="text-muted-foreground">
                        Saved to deck:
                      </div>
                      <div class="font-medium text-foreground">
                        Q: {{ card.question }}
                      </div>
                      <div
                        v-if="expandedFlashcards.has(card.id)"
                        class="mt-1 text-muted-foreground"
                      >
                        A: {{ card.answer }}
                      </div>
                      <div class="mt-1 text-muted-foreground text-[10px]">
                        {{ expandedFlashcards.has(card.id) ? 'click to collapse' : 'click to expand answer' }}
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom dock: error / continue / input — aligned with reading column -->
      <div class="shrink-0 px-6 pb-4">
        <div class="max-w-3xl mx-auto w-full">
          <!-- Error display -->
          <div
            v-if="error"
            class="bg-destructive/10 text-destructive p-3 rounded-md mb-3 text-sm"
          >
            {{ error }}
          </div>

          <!-- Hand off to the dedicated retention chat for a completed lesson -->
          <div
            v-if="showRetentionHandoff"
            class="pb-3 flex justify-center"
          >
            <Button
              type="button"
              variant="outline"
              @click="goToRetention"
            >
              Choose what to retain &rarr;
            </Button>
          </div>

          <!-- Continue to next lesson -->
          <div
            v-if="showContinueButton && nextLesson"
            class="pb-3 flex justify-center"
          >
            <Button
              type="button"
              @click="continueToNextLesson"
            >
              Continue to Lesson {{ nextLesson.order + 1 }}: {{ nextLesson.title }}
            </Button>
          </div>

          <!-- Input area -->
          <form
            class="flex items-end gap-2"
            @submit.prevent="handleSubmit"
          >
            <textarea
              ref="textareaRef"
              v-model="inputText"
              :disabled="isStreaming"
              rows="1"
              class="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 resize-none max-h-40 overflow-y-auto scrollbar-thin"
              placeholder="Type your answer..."
              @keydown.enter.exact.prevent="handleSubmit"
              @input="autoResize"
            />
            <Button
              type="submit"
              :disabled="isStreaming || !inputText.trim()"
            >
              Send
            </Button>
          </form>
        </div>
      </div>
    </template>
  </AppLayout>
</template>
